import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API_URL, formatDate, getStatusBadgeClass } from '../lib/utils';
import { toast } from 'sonner';
import {
  Briefcase,
  Users,
  FileText,
  Loader2,
  Building2,
  UserCheck,
  TrendingUp,
  DollarSign,
  Settings,
  CreditCard,
  Shield,
  Upload,
  Rss,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Mail,
  MessageSquare,
  MousePointerClick,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Send,
  Clock,
  BadgeCheck,
  ExternalLink,
  XCircle,
  MapPin,
  Bell,
  Eye,
  Plug,
  GraduationCap,
  ShieldCheck,
} from 'lucide-react';

import { AdminCtx } from './AdminContext';
import AdminTabOverview from './AdminTabOverview';
import AdminStatistics from './admin/AdminStatistics';
import AdminTabSubscriptions from './AdminTabSubscriptions';
import AdminTabTransactions from './AdminTabTransactions';
import AdminTabUsers from './AdminTabUsers';
import AdminTabJobs from './AdminTabJobs';
import AdminTabVerification from './AdminTabVerification';
import AdminTabEmployers from './AdminTabEmployers';
import AdminTabImport from './AdminTabImport';
import AdminTabSeoHealth from './AdminTabSeoHealth';
import AdminTabAnalytics from './AdminTabAnalytics';
import AdminTabDigest from './AdminTabDigest';
import AdminTabSettings from './AdminTabSettings';
import AdminTabATS from './AdminTabATS';
import AdminTabMarketplace from './AdminTabMarketplace';
import AdminTabOnboarding from './AdminTabOnboarding';
import AdminTabParentQueue from './AdminTabParentQueue';
import AdminTabPrivatePractice from './AdminTabPrivatePractice';
import AdminTabInternship from './AdminTabInternship';
import AdminTabInternshipQC from './AdminTabInternshipQC';

const AdminDashboard = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [ownerSettings, setOwnerSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Import states
  const [rssFeeds, setRssFeeds] = useState({});
  const [customRssUrl, setCustomRssUrl] = useState('');
  const [importingRss, setImportingRss] = useState(false);
  const [importingCsv, setImportingCsv] = useState(false);
  const [csvTemplate, setCsvTemplate] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const csvFileRef = useRef(null);
  
  // Analytics states
  const [contactAnalytics, setContactAnalytics] = useState(null);
  const [contactMessages, setContactMessages] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  
  // Digest states
  const [digestSettings, setDigestSettings] = useState(null);
  const [digestHistory, setDigestHistory] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [savingDigest, setSavingDigest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [scheduleTime, setScheduleTime] = useState({ hour: 9, minute: 0 });
  
  // License verification states
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verifyingUser, setVerifyingUser] = useState(null);
  const [verificationSourceUrl, setVerificationSourceUrl] = useState('');
  
  // Verified employers states
  const [employers, setEmployers] = useState([]);
  const [employersLoading, setEmployersLoading] = useState(false);
  const [verifyingEmployer, setVerifyingEmployer] = useState(null);

  // Priority Signups (Leads) states
  const [prioritySignups, setPrioritySignups] = useState([]);
  const [prioritySignupsLoading, setPrioritySignupsLoading] = useState(false);
  const [signupsByState, setSignupsByState] = useState({});
  
  // Site visits stats
  const [siteVisits, setSiteVisits] = useState({
    total_visits: 0,
    unique_visitors: 0,
    today_visits: 0,
    daily_visits: []
  });
  const [contentStats, setContentStats] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const fetchData = async () => {
    try {
      const headers = {  };
      
      const [statsRes, usersRes, jobsRes, txRes, subsRes, settingsRes, rssFeedsRes, csvTemplateRes, signupsRes, visitsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`, { headers }),
        axios.get(`${API_URL}/admin/users`, { headers }),
        axios.get(`${API_URL}/admin/jobs`, { headers }),
        axios.get(`${API_URL}/admin/transactions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/subscriptions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/settings`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/admin/import/rss-feeds`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/admin/import/csv-template`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/admin/priority-signups?limit=200`, { headers }).catch(() => ({ data: { signups: [], total: 0 } })),
        axios.get(`${API_URL}/admin/site-visits?days=30`, { headers }).catch(() => ({ data: { total_visits: 0, unique_visitors: 0, today_visits: 0, daily_visits: [] } })),
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setJobs(jobsRes.data);
      setTransactions(txRes.data);
      setSubscriptions(subsRes.data);
      setOwnerSettings(settingsRes.data);
      setRssFeeds(rssFeedsRes.data);
      setCsvTemplate(csvTemplateRes.data);
      setSiteVisits(visitsRes.data);
      axios.get(`${API_URL}/content-stats`).then((r) => setContentStats(r.data)).catch(() => {});
      
      // Process priority signups
      const signups = signupsRes.data.signups || [];
      setPrioritySignups(signups);
      
      // Group by state for the widget
      const byState = signups.reduce((acc, signup) => {
        const state = signup.state || 'General';
        if (!acc[state]) acc[state] = [];
        acc[state].push(signup);
        return acc;
      }, {});
      setSignupsByState(byState);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch employers for verification management
  const fetchEmployers = async () => {
    setEmployersLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/employers`, {
        headers: {  }
      });
      setEmployers(response.data.employers || []);
    } catch (error) {
      console.error('Error fetching employers:', error);
      toast.error('Failed to load employers');
    } finally {
      setEmployersLoading(false);
    }
  };

  // Verify/Unverify employer
  const handleVerifyEmployer = async (userId, verify = true) => {
    setVerifyingEmployer(userId);
    try {
      const endpoint = verify 
        ? `${API_URL}/admin/employers/${userId}/verify`
        : `${API_URL}/admin/employers/${userId}/unverify`;
      
      await axios.put(endpoint, {}, {
        headers: {  }
      });
      
      toast.success(verify ? 'Employer verified!' : 'Verification removed');
      fetchEmployers(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update verification');
    } finally {
      setVerifyingEmployer(null);
    }
  };

  const handleImportRss = async (feedUrl) => {
    setImportingRss(true);
    setImportResult(null);
    try {
      const response = await axios.post(
        `${API_URL}/admin/import/rss`,
        { feed_url: feedUrl || customRssUrl },
        { headers: {  } }
      );
      setImportResult({ type: 'success', ...response.data });
      toast.success(`Successfully imported ${response.data.count} jobs!`);
      fetchData(); // Refresh jobs list
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to import jobs';
      setImportResult({ type: 'error', message });
      toast.error(message);
    } finally {
      setImportingRss(false);
    }
  };

  const handleImportCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setImportingCsv(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/admin/import/csv`,
        formData,
        { 
          headers: { 
            
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      setImportResult({ 
        type: response.data.errors?.length > 0 ? 'partial' : 'success', 
        ...response.data 
      });
      
      if (response.data.count > 0) {
        toast.success(`Successfully imported ${response.data.count} jobs!`);
        fetchData(); // Refresh jobs list
      }
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to import CSV';
      setImportResult({ type: 'error', message });
      toast.error(message);
    } finally {
      setImportingCsv(false);
      if (csvFileRef.current) csvFileRef.current.value = '';
    }
  };

  const downloadCsvTemplate = () => {
    if (!csvTemplate) return;
    
    const headers = csvTemplate.columns.join(',');
    const exampleRow = csvTemplate.columns.map(col => csvTemplate.example_row[col] || '').join(',');
    const csvContent = `${headers}\n${exampleRow}`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'job_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  const fetchContactAnalytics = async (days = 30) => {
    setAnalyticsLoading(true);
    try {
      const headers = {  };
      
      const [analyticsRes, messagesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/analytics/contact?days=${days}`, { headers }),
        axios.get(`${API_URL}/admin/analytics/contact/messages?limit=10`, { headers }),
      ]);
      
      setContactAnalytics(analyticsRes.data);
      setContactMessages(messagesRes.data.messages || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const updateMessageStatus = async (contactId, status) => {
    try {
      await axios.put(
        `${API_URL}/admin/analytics/contact/messages/${contactId}`,
        { status },
        { headers: {  } }
      );
      toast.success('Status updated');
      fetchContactAnalytics(analyticsDays);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchContactAnalytics(analyticsDays);
    }
    if (activeTab === 'digest') {
      fetchDigestSettings();
    }
    if (activeTab === 'verification') {
      fetchPendingVerifications();
    }
  }, [activeTab, analyticsDays]);

  const fetchPendingVerifications = async () => {
    setVerificationLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/pending-verifications`, {
        headers: {  },
      });
      setPendingVerifications(response.data.pending_verifications || []);
    } catch (error) {
      console.error('Error fetching pending verifications:', error);
      toast.error('Failed to load pending verifications');
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleVerifyLicense = async (userId, status) => {
    setVerifyingUser(userId);
    try {
      await axios.put(
        `${API_URL}/admin/verify-license/${userId}`,
        { 
          status, 
          notes: verificationSourceUrl ? `Verified at: ${verificationSourceUrl}` : ''
        },
        { headers: {  } }
      );
      toast.success(`License ${status === 'verified' ? 'verified' : 'rejected'} successfully!`);
      setVerificationSourceUrl('');
      // Remove from list
      setPendingVerifications(prev => prev.filter(u => u.user_id !== userId));
    } catch (error) {
      toast.error('Failed to update verification status');
    } finally {
      setVerifyingUser(null);
    }
  };

  const fetchDigestSettings = async () => {
    try {
      const headers = {  };
      
      const [settingsRes, historyRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/admin/digest/settings`, { headers }),
        axios.get(`${API_URL}/admin/digest/history?limit=5`, { headers }),
        axios.get(`${API_URL}/admin/digest/scheduler-status`, { headers }),
      ]);
      
      setDigestSettings(settingsRes.data);
      setDigestHistory(historyRes.data.logs || []);
      setSchedulerStatus(statusRes.data);
      
      // Parse send_time if available
      if (settingsRes.data.send_time) {
        const [h, m] = settingsRes.data.send_time.split(':').map(Number);
        setScheduleTime({ hour: h, minute: m });
      }
    } catch (error) {
      console.error('Error fetching digest settings:', error);
    }
  };

  const handleSaveDigestSettings = async () => {
    setSavingDigest(true);
    try {
      await axios.put(`${API_URL}/admin/digest/settings`, digestSettings, {
        headers: {  },
      });
      toast.success('Digest settings saved!');
    } catch (error) {
      toast.error('Failed to save digest settings');
    } finally {
      setSavingDigest(false);
    }
  };

  const handleSendDigestNow = async (period) => {
    setSendingDigest(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/digest/send-now`,
        { period },
        { headers: {  } }
      );
      toast.success(`${period === 'daily' ? 'Daily' : 'Weekly'} digest sent successfully!`);
      fetchDigestSettings(); // Refresh history
    } catch (error) {
      toast.error('Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const handleUpdateSchedule = async () => {
    try {
      await axios.post(
        `${API_URL}/admin/digest/update-schedule`,
        scheduleTime,
        { headers: {  } }
      );
      toast.success('Schedule updated!');
      fetchDigestSettings();
    } catch (error) {
      toast.error('Failed to update schedule');
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API_URL}/admin/settings`, ownerSettings, {
        headers: {  },
      });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const totalRevenue = transactions
    .filter(t => t.payment_status === 'paid')
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  const activeSubscriptions = subscriptions.filter(s => s.status === 'active').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-bold text-gray-900">
            Admin Dashboard
          </h1>
          <p className="text-gray-600">Manage your platform</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <button type="button" onClick={() => setActiveTab('transactions')} className="text-left bg-white rounded-xl p-5 border border-gray-100 hover:border-primary/40 hover:shadow-sm transition-all" data-testid="stat-card-revenue">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">${totalRevenue.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Total Revenue</p>
              </div>
            </div>
          </button>
          <button type="button" onClick={() => setActiveTab('subscriptions')} className="text-left bg-white rounded-xl p-5 border border-gray-100 hover:border-primary/40 hover:shadow-sm transition-all" data-testid="stat-card-subscriptions">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{activeSubscriptions}</p>
                <p className="text-xs text-gray-500">Active Subscriptions</p>
              </div>
            </div>
          </button>
          <button type="button" onClick={() => setActiveTab('users')} className="text-left bg-white rounded-xl p-5 border border-gray-100 hover:border-primary/40 hover:shadow-sm transition-all" data-testid="stat-card-users">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_users || 0}</p>
                <p className="text-xs text-gray-500">Total Users</p>
              </div>
            </div>
          </button>
          <button type="button" onClick={() => setActiveTab('jobs')} className="text-left bg-white rounded-xl p-5 border border-gray-100 hover:border-primary/40 hover:shadow-sm transition-all" data-testid="stat-card-jobs">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{(contentStats?.active_jobs_total ?? stats?.active_jobs ?? 0).toLocaleString()}</p>
                <p className="text-xs text-gray-500">Active Jobs</p>
              </div>
            </div>
            {contentStats && (
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4 text-xs" data-testid="job-breakdown">
                <span className="flex items-center gap-1 text-gray-600">
                  <span className="w-2 h-2 rounded-full bg-secondary inline-block" />
                  <strong className="text-gray-900">{(contentStats.employer_jobs_total || 0).toLocaleString()}</strong> Employer&nbsp;Posted
                </span>
                <span className="flex items-center gap-1 text-gray-600">
                  <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />
                  <strong className="text-gray-900">{(contentStats.imported_jobs_total || 0).toLocaleString()}</strong> Imported
                </span>
              </div>
            )}
          </button>
        </div>

        {/* Tabs */}
        <AdminCtx.Provider value={{ activeSubscriptions, activeTab, analyticsDays, analyticsLoading, contactAnalytics, contactMessages, csvFileRef, csvTemplate, customRssUrl, digestHistory, digestSettings, downloadCsvTemplate, employers, employersLoading, fetchContactAnalytics, fetchData, fetchDigestSettings, fetchEmployers, fetchPendingVerifications, handleImportCsv, handleImportRss, handleSaveDigestSettings, handleSaveSettings, handleSendDigestNow, handleUpdateSchedule, handleVerifyEmployer, handleVerifyLicense, importResult, importingCsv, importingRss, isAdmin, jobs, loading, navigate, ownerSettings, pendingVerifications, prioritySignups, prioritySignupsLoading, rssFeeds, savingDigest, savingSettings, scheduleTime, schedulerStatus, sendingDigest, setActiveTab, setAnalyticsDays, setAnalyticsLoading, setContactAnalytics, setContactMessages, setCsvTemplate, setCustomRssUrl, setDigestHistory, setDigestSettings, setEmployers, setEmployersLoading, setImportResult, setImportingCsv, setImportingRss, setJobs, setLoading, setOwnerSettings, setPendingVerifications, setPrioritySignups, setPrioritySignupsLoading, setRssFeeds, setSavingDigest, setSavingSettings, setScheduleTime, setSchedulerStatus, setSendingDigest, setSignupsByState, setSiteVisits, setStats, setSubscriptions, setTransactions, setUsers, setVerificationLoading, setVerificationSourceUrl, setVerifyingEmployer, setVerifyingUser, signupsByState, siteVisits, stats, subscriptions, totalRevenue, transactions, updateMessageStatus, user, users, verificationLoading, verificationSourceUrl, verifyingEmployer, verifyingUser }}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-gray-100 p-1 mb-6 flex flex-nowrap overflow-x-auto scrollbar-hide max-w-full justify-start gap-1">
            <TabsTrigger value="overview" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white">
              Overview
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white">
              Subscriptions
            </TabsTrigger>
            <TabsTrigger value="transactions" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white">
              Transactions
            </TabsTrigger>
            <TabsTrigger value="users" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="admin-tab-users">
              Users &amp; Sign-ups
            </TabsTrigger>
            <TabsTrigger value="jobs" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white">
              Jobs
            </TabsTrigger>
            <TabsTrigger value="verification" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white">
              <BadgeCheck className="w-4 h-4 mr-1" />
              License Verification
            </TabsTrigger>
            <TabsTrigger value="employers" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white" onClick={() => fetchEmployers()}>
              <Building2 className="w-4 h-4 mr-1" />
              Verified Employers
            </TabsTrigger>
            <TabsTrigger value="import" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white">
              Import Jobs
            </TabsTrigger>
            <TabsTrigger value="analytics" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white">
              Contact Analytics
            </TabsTrigger>
            <TabsTrigger value="digest" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white">
              Email Digest
            </TabsTrigger>
            <TabsTrigger value="ats" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white">
              <Plug className="w-4 h-4 mr-1" />
              ATS Requests
            </TabsTrigger>
            <TabsTrigger value="marketplace" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="admin-tab-marketplace">
              <GraduationCap className="w-4 h-4 mr-1" />
              Supervisors &amp; Internships
            </TabsTrigger>
            <TabsTrigger value="settings" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white">
              Settings
            </TabsTrigger>
            <TabsTrigger value="onboarding" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="admin-tab-onboarding">
              <Mail className="w-4 h-4 mr-1" />
              Onboarding &amp; Emails
            </TabsTrigger>
            <TabsTrigger value="resource-queue" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="admin-tab-resource-queue">
              <Users className="w-4 h-4 mr-1" />
              Parent Resources
            </TabsTrigger>
            <TabsTrigger value="private-practice" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="admin-tab-private-practice">
              <Building2 className="w-4 h-4 mr-1" />
              Private Practice
            </TabsTrigger>
            <TabsTrigger value="internship-portal" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="admin-tab-internship-portal">
              <GraduationCap className="w-4 h-4 mr-1" />
              Internship Portal
            </TabsTrigger>
            <TabsTrigger value="internship-qc" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="admin-tab-internship-qc">
              <ShieldCheck className="w-4 h-4 mr-1" />
              Internship QC
            </TabsTrigger>
            <TabsTrigger value="statistics" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="admin-tab-statistics">
              <BarChart3 className="w-4 h-4 mr-1" />
              INASW Statistics
            </TabsTrigger>
            <TabsTrigger value="seo-health" className="whitespace-nowrap flex-shrink-0 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="admin-tab-seo-health">
              <BarChart3 className="w-4 h-4 mr-1" />
              SEO Health
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <AdminTabOverview />
          </TabsContent>

          {/* SEO Health Tab */}
          <TabsContent value="seo-health">
            <AdminTabSeoHealth />
          </TabsContent>

          {/* Private Practice Tab */}
          <TabsContent value="private-practice">
            <AdminTabPrivatePractice />
          </TabsContent>

          {/* Internship Portal Tab */}
          <TabsContent value="internship-portal">
            <AdminTabInternship />
          </TabsContent>

          {/* Internship QC Tab */}
          <TabsContent value="internship-qc">
            <AdminTabInternshipQC />
          </TabsContent>

          {/* INASW Statistics Tab */}
          <TabsContent value="statistics">
            <AdminStatistics />
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <AdminTabSubscriptions />
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <AdminTabTransactions />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <AdminTabUsers />
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <AdminTabJobs />
          </TabsContent>

          {/* License Verification Tab */}
          <TabsContent value="verification">
            <AdminTabVerification />
          </TabsContent>

          {/* Verified Employers Tab */}
          <TabsContent value="employers">
            <AdminTabEmployers />
          </TabsContent>

          {/* Import Jobs Tab */}
          <TabsContent value="import">
            <AdminTabImport />
          </TabsContent>

          {/* Contact Analytics Tab */}
          <TabsContent value="analytics">
            <AdminTabAnalytics />
          </TabsContent>

          {/* Email Digest Tab */}
          <TabsContent value="digest">
            <AdminTabDigest />
          </TabsContent>

          {/* ATS Integration Requests Tab */}
          <TabsContent value="ats">
            <AdminTabATS />
          </TabsContent>

          {/* Supervisor & Internship Marketplace Tab */}
          <TabsContent value="marketplace">
            <AdminTabMarketplace />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <AdminTabSettings />
          </TabsContent>

          {/* Onboarding & Emails Tab */}
          <TabsContent value="onboarding">
            <AdminTabOnboarding />
          </TabsContent>

          {/* Resource Review Queue Tab */}
          <TabsContent value="resource-queue">
            <AdminTabParentQueue />
          </TabsContent>
        </Tabs>
        </AdminCtx.Provider>
      </div>
    </div>
  );
};

export default AdminDashboard;
